import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';

/**
 * Phase 5f-3 — Submit-for-review panel.
 *
 * Renders only for caregivers in APPLYING status. Reads
 * GET /me/caregiver/application-readiness for the missing checklist;
 * the Submit button is enabled when `ready === true`. On click,
 * POSTs to /me/caregiver/profile/submit (APPLYING → UNDER_REVIEW).
 *
 * Per 5f-3: the readiness endpoint is the single source of truth for
 * what's missing; portal renders the codes from the activation
 * matrix into a human-readable checklist.
 */

/** Phase 5f-d audit r1 — backend returns the user-facing `reason`
 *  on each missing[] item (structured shape per 5f-3 lock). The
 *  portal renders it verbatim — no client-side label map; i18n /
 *  wording changes ship from backend without portal release. */

export function CaregiverSubmitPanel() {
  const qc = useQueryClient();

  const readinessQuery = useQuery({
    queryKey: ['caregiver', 'application-readiness'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/application-readiness')),
  });

  const submitMutation = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/me/caregiver/profile/submit')),
    onSuccess: () => {
      // Both queries change: profile.status flips to UNDER_REVIEW,
      // and readiness re-reports the new status.
      qc.invalidateQueries({ queryKey: ['caregiver', 'me'] });
      qc.invalidateQueries({ queryKey: ['caregiver', 'application-readiness'] });
    },
  });

  if (readinessQuery.isPending) {
    return (
      <Card padding="lg">
        <p className="font-body text-sm text-ink-muted">Checking application readiness…</p>
      </Card>
    );
  }

  if (readinessQuery.isError || !readinessQuery.data) {
    return (
      <Card padding="lg">
        <Alert tone="danger" title="Couldn't load application status">
          Please refresh the page.
        </Alert>
      </Card>
    );
  }

  const { ready, missing, status } = readinessQuery.data;

  // Panel only relevant during APPLYING; the parent /me page only
  // mounts it for that status, but render defensively in case it's
  // mounted for UNDER_REVIEW etc.
  if (status !== 'APPLYING') {
    return null;
  }

  return (
    <Card padding="lg" data-testid="caregiver-submit-panel">
      <CardHeader
        title="Submit for review"
        description={
          ready
            ? "You've uploaded everything needed. Click Submit to send your application to TrueCare™."
            : 'A few items still need attention before you can submit:'
        }
      />

      {!ready && missing.length > 0 && (
        <ul
          className="mt-4 space-y-2 list-disc pl-5 font-body text-sm text-ink"
          data-testid="caregiver-submit-missing-list"
        >
          {missing.map((item: { category: string; type: string; reason: string }) => {
            const key = `${item.category}:${item.type}`;
            return (
              <li key={key} data-testid={`caregiver-submit-missing-${key}`}>
                {item.reason}
              </li>
            );
          })}
        </ul>
      )}

      {submitMutation.isError && (
        <Alert tone="danger" className="mt-4" title="Couldn't submit your application">
          {submitMutation.error instanceof Error
            ? submitMutation.error.message
            : 'Please try again in a moment.'}
        </Alert>
      )}

      <div className="mt-5 flex justify-end">
        <Button
          type="button"
          variant="primary"
          disabled={!ready}
          loading={submitMutation.isPending}
          onClick={() => submitMutation.mutate()}
          data-testid="caregiver-submit-button"
        >
          Submit for review
        </Button>
      </div>
    </Card>
  );
}
